import { ActionTree, ActionContext } from 'vuex'
import {
  fetchNewCourses,
  fetchCourseById,
  fetchAllCourses,
  fetchActiveCourses,
  postActiveCourse,
  deleteActiveCourse,
  fetchCourseRating,
  postCourseRating,
  updateCourseRating,
  fetchInstructorById,
  fetchContentIssues,
  postContentIssue,
  updateCourseUserPreferences,
} from '../../../models/courses'
import { CoursesState } from './state'
import { Level } from '../../types'

const actions: ActionTree<CoursesState, {}> = {
  getActiveCourses: async (context: ActionContext<CoursesState, {}>) => {
    try {
      const response = await fetchActiveCourses()
      context.commit('ACTIVE_COURSES_UPDATED', response)
    } catch (error) {
      const { message } = error
      context.dispatch('app/setError', message, { root: true })
    }
  },

  updateCurrentLevels: (
    context: ActionContext<CoursesState, {}>,
    level: Level
  ) => {
    context.commit('CURRENT_LEVELS_UPDATED', level)
  },

  addActiveCourse: async (
    context: ActionContext<CoursesState, {}>,
    courseId: string
  ) => {
    try {
      await postActiveCourse(courseId)
      context.dispatch(
        'app/setNotification',
        'The course was added to your active courses.',
        {
          root: true,
        }
      )
    } catch (error) {
      const { message } = error
      context.dispatch('app/setError', message, { root: true })
    }
  },

  removeActiveCourse: async (
    context: ActionContext<CoursesState, {}>,
    courseId: string
  ) => {
    try {
      await deleteActiveCourse(courseId)
      context.dispatch(
        'app/setNotification',
        'The course was removed from your active courses.',
        {
          root: true,
        }
      )
    } catch (error) {
      const { message } = error
      context.dispatch('app/setError', message, { root: true })
    }
  },

  getCourseById: async (
    context: ActionContext<CoursesState, {}>,
    id: string
  ) => {
    try {
      const response = await fetchCourseById(id)
      context.commit('CURRENT_COURSE_UPDATED', response)
      if (response && response.user_rating) {
        context.commit('COURSE_COURSE_RATING_UPDATED', response.user_rating)
      }
      if (response && response.user_status) {
        context.commit('COURSE_PROGRESS_UPDATED', response.user_status)
      } else {
        context.commit('COURSE_PROGRESS_UPDATED', {})
      }
      return response
    } catch (error) {
      const { message } = error
      context.dispatch('app/setError', message, { root: true })
    }
  },

  getRatingById: async (context: ActionContext<CoursesState, {}>, payload) => {
    try {
      const response = await fetchCourseRating(payload)
      context.commit('COURSE_COURSE_RATING_UPDATED', response)
      return response
    } catch (error) {
      const { message } = error
      context.dispatch('app/setError', message, {
        root: true,
      })
    }
  },

  saveCourseRating: async (
    context: ActionContext<CoursesState, {}>,
    payload
  ) => {
    try {
      const { id: ratingId } = payload
      if (!payload.instructor_rating) delete payload.instructor_rating
      if (!payload.course_rating) delete payload.course_rating
      let response = null
      if (!ratingId) {
        response = await postCourseRating(payload)
      } else {
        response = await updateCourseRating(payload)
      }
      context.commit('COURSE_COURSE_RATING_UPDATED', response)
      return response
    } catch (error) {
      const { message } = error
      context.dispatch('app/setError', message, {
        root: true,
      })
    }
  },

  getInstructorById: async (
    context: ActionContext<CoursesState, {}>,
    instructorId: string
  ) => {
    try {
      const response = await fetchInstructorById(instructorId)
      return response
    } catch (error) {
      const { message } = error
      context.dispatch('app/setError', message, {
        root: true,
      })
    }
  },

  getContentIssues: async (context: ActionContext<CoursesState, {}>) => {
    try {
      const response = await fetchContentIssues()
      context.commit('UPDATE_ALL_ISSUES', response)
    } catch (error) {
      const { message } = error
      context.dispatch('app/setError', message, {
        root: true,
      })
    }
  },

  sendContentIssue: async (
    context: ActionContext<CoursesState, {}>,
    issue: object
  ) => {
    try {
      const payload = {
        ...context.state.currentIssue,
        ...issue,
      }
      const response = await postContentIssue(payload)

      context.dispatch(
        'content/sendMixpanelEvent',
        {
          event_name: 'content.issue.sent',
          properties: {
            ...context.state.currentIssue,
          },
        },
        { root: true }
      )
      return response
    } catch (error) {
      const { message } = error
      context.dispatch('app/setError', message, {
        root: true,
      })
    }
  },

  saveContentIssue: (context: ActionContext<CoursesState, {}>, payload) => {
    context.commit('CURRENT_ISSUE_PAYLOAD_UPDATED', payload)
  },

  resetContentIssue: (context: ActionContext<CoursesState, {}>) => {
    context.commit('CURRENT_ISSUE_RESET')
  },

  toggleRatingModal: (context: ActionContext<CoursesState, {}>, status) => {
    context.commit('TOGGLE_RATING_MODAL', status)
  },

  sendCourseUserPreferences: async (
    context: ActionContext<CoursesState, {}>,
    payload
  ) => {
    const response = await updateCourseUserPreferences({
      courseId: context.state.currentCourse.id,
      preferences: payload,
    })
    context.commit('UPDATE_USER_STATUS_INFO', response)
    return response
  },

  toggleUserStatusInfo(context: ActionContext<CoursesState, {}>, payload) {
    context.commit('UPDATE_USER_STATUS_INFO', payload)
  },
}

export default actions
